import { api, type ApiResponse } from './client';

// ── Types ──

export interface AlibabaSearch {
  id: number;
  search_id: string;
  project_id?: string;
  keyword: string;
  category_id?: string;
  category_name?: string;
  page: number;
  total_pages?: number;
  search_url?: string;
  language: string;
  product_count: number;
  html_size?: number;
  source: string;
  status: 'pending' | 'crawling' | 'parsing' | 'completed' | 'failed';
  error_message?: string;
  crawl_time: string;
  created_at: string;
  health_status?: 'healthy' | 'warning' | 'error' | 'unknown';
  health_issues?: string[];
  retry_count?: number;
  retry_source?: string;
}

export interface AlibabaProduct {
  id: number;
  search_id: string;
  position: number;
  product_id: string;
  title: string;
  slug?: string;
  url?: string;
  image_url?: string;
  price_current?: string;
  price_original?: string;
  price_min?: number;
  price_max?: number;
  price_currency: string;
  discount?: string;
  moq_text?: string;
  moq_quantity?: number;
  moq_unit?: string;
  company?: string;
  company_url?: string;
  years_in_business?: number;
  selling_points?: string[];
  has_trade_assurance: boolean;
  rating?: number;
  task_id?: string;
  seo_analyzed: boolean;
  seo_score?: number;
  created_at: string;
}

export interface AlibabaFilter {
  id: number;
  keyword: string;
  category_id: string;
  group_id: string;
  group_title: string;
  filter_type: 'switch' | 'radio' | 'input' | 'category';
  items_json: Array<{ label: string; value: string; count?: number }>;
  item_count: number;
}

export interface AlibabaRelatedKeyword {
  id: number;
  keyword: string;
  source_keyword: string;
  category_id: string;
  position: number;
}

export interface AlibabaSearchDetail extends AlibabaSearch {
  products: AlibabaProduct[];
  related_keywords: AlibabaRelatedKeyword[];
}

export interface UrlDetailItem {
  url: string;
  domain: string;
  rank_absolute: number;
  rank_group: 'top' | 'non_top';
  scraping_task_id: string | null;
  stage_done: boolean;
  html_ssr_done: boolean;
  h_structure_done: boolean;
  markdown_done: boolean;
  nav_tree_done: boolean;
  schema_done: boolean;
  images_count: number;
  last_scraped_at: string | null;
  task_status: string | null;
  task_error: string | null;
  is_blocklisted: boolean;
  blocklist_status?: 'active' | 'historical' | null;
  serp_request_id?: string | null;
}

export interface AliStepDetailItem {
  task_id: string;
  url: string;
  status: 'completed' | 'completed_empty' | 'failed' | 'running' | 'pending' | 'skipped' | 'not_applicable' | 'not_started';
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface AliStepDetailResponse {
  keyword: string;
  step_id: string;
  total: number;
  summary: Record<string, number>;
  items: AliStepDetailItem[];
}

// PD step tooltip — correct source per PD-1~PD-10
export interface AliPdStepTooltipItem {
  title: string;
  url: string;
  status: string;
  field_value: string | null;
}
export interface AliPdStepTooltipResponse {
  keyword: string;
  pd_step: number;
  field_label: string;
  total: number;
  summary: Record<string, number>;
  items: AliPdStepTooltipItem[];
}

// ── AliPD Panel types (Tab3 click-through panel) ──
export interface AliPdStep {
  step_id: string
  step_name: string
  series: string
  step_order: number
  status: string
  duration_ms: number | null
  output_summary: string | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
  worker_id: string | null
}

export interface AliPdUrlItem {
  task_id: string
  url: string | null
  domain: string
  rank_absolute: number
  rank_group: 'top' | 'non_top'
  steps: AliPdStep[]
}

export interface KeywordAllStepsResponse {
  keyword: string
  total_urls: number    // 全部筆數（不分頁）
  page_urls: number     // 本頁筆數
  urls: AliPdUrlItem[]
}

// ── Ali search progress (路徑 A: category page crawl) ──
export interface AliSearchProgressRow {
  keyword: string;
  total_pages: number;
  pages_done: number;
  pages_failed: number;
  pages_pending: number;
  products_found: number;
  company_urls_found: number;
}

// ── Ali company page progress (路徑 C: company profile crawl) ──
export interface AliCompanyProgressRow {
  keyword: string;
  company_urls_found: number;
  profiles_crawled: number;
  nav_crawled: number;
  profiles_failed: number;
}

// ── Drill-down response types ──

/** SC Drill-down: per-page breakdown for a keyword */
export interface ScPageRow {
  search_id:         string
  page_number:       number
  search_url:        string | null
  status:            string | null
  product_count:     number
  company_url_count: number
  created_at:        string | null
  updated_at:        string | null
}

/** PD Drill-down: per-product from alibaba_product_profiles (correct source) */
export interface PdProductRow {
  id:                  number
  url:                 string | null
  title:               string | null       // ← 前台左側第一欄用這個
  company:             string | null
  company_url:         string | null
  image_url:           string | null
  position:            number | null
  task_id:             string | null
  task_status:         string | null       // scraping_tasks.status (BA 爬取狀態)
  parse_status:        string | null       // alibaba_product_profiles.parse_status
  has_iframe:          boolean             // iframe_url IS NOT NULL
  iframe_image_count:  number              // 0 if null
  has_screenshot:      boolean             // screenshot_url IS NOT NULL
  has_markdown:        boolean             // html_full_markdown IS NOT NULL
  smi_done:            boolean             // smi_uploaded_at IS NOT NULL
  profile_parsed_at:   string | null
  stage_status:        Record<string, string> | null  // 保留 scraping_tasks.stage_status 供參考
  selected_stages:     string[] | null
}

/** CO Drill-down: per-company CO-1/CO-2 status */
export interface CoCompanyRow {
  company_url:        string
  company_name:       string | null
  profile_status:     string | null   // QS crawl status: completed / failed / pending / null
  nav_parsed:         boolean          // BA: nav_json exists
  nav_category_count: number
  updated_at:         string | null
  rating:             number | null    // 評分
  gold_supplier:      boolean | null   // Gold廠商
  is_factory:         boolean | null   // 工廠
  cert_count:         number           // 認證數
}

/** CO Nav category detail item (from ali_company_nav_categories) */
export interface CoNavCategory {
  category_name: string
  category_url:  string | null
  nav_level:     number | null
}

/** Ali PD Keyword Matrix row: per-keyword aggregation from alibaba_searches + alibaba_products */
export interface AliPdMatrixRow {
  keyword:        string
  total_products: number
  pd1_done:  number
  pd2_done:  number
  pd3_done:  number
  pd4_done:  number
  pd5_done:  number
  pd6_done:  number
  pd7_done:  number
  pd8_done:  number
  pd9_done:  number
  pd10_done: number
  completion_pct: number | null
}

export interface AlibabaSearchFilters {
  limit?: number;
  offset?: number;
  status?: string;
  project_id?: string;
  keyword?: string;
  archived?: boolean;
}

// ── Query builder ──

function buildQuery(filters: AlibabaSearchFilters): string {
  const params = new URLSearchParams();
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  if (filters.status) params.set('status', filters.status);
  if (filters.project_id) params.set('project_id', filters.project_id);
  if (filters.keyword) params.set('keyword', filters.keyword);
  if (filters.archived) params.set('archived', 'true');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── API ──

export const alibabaApi = {
  /** Start a new Alibaba search (triggers BrowserAct crawl + parse, ~30-60s/page) */
  search: (data: {
    keyword: string;
    category_id?: string;
    language?: string;
    pages?: number;
    project_id?: string;
    selected_stages?: string[];
    force_rescan?: boolean;       // bypass 12h dedup cache
    specific_pages?: number[];    // crawl only these page numbers instead of 1-N
    skip_existing_urls?: boolean; // skip products already in DB for this keyword
  }) =>
    api.post<{
      keyword: string;
      page_count: number;
      total_products: number;
      pages: Array<{
        search_id: string;
        page: number;
        product_count: number;
        filter_groups: number;
        related_keywords: number;
        status: string;
      }>;
    }>('/alibaba/search', data),

  /** List search history */
  searches: (filters: AlibabaSearchFilters = {}) =>
    api.get<AlibabaSearch[]>(`/alibaba/searches${buildQuery(filters)}`),

  /** Get search detail with products */
  getSearch: (id: string) =>
    api.get<AlibabaSearchDetail>(`/alibaba/searches/${id}`),

  /** Get filters for a search */
  getFilters: (id: string) =>
    api.get<AlibabaFilter[]>(`/alibaba/searches/${id}/filters`),

  /** Batch create SEO analysis tasks from selected products */
  analyzeProducts: (data: { product_ids: number[]; project_id?: string }) =>
    api.post<{ created: number; task_ids: string[] }>('/alibaba/products/analyze', data),

  /** Delete all searches by keyword */
  deleteByKeyword: (keyword: string) =>
    api.delete<{ keyword: string; deleted: number }>(`/alibaba/searches/by-keyword/${encodeURIComponent(keyword)}`),

  /** Archive/unarchive by keyword */
  archiveByKeyword: (keyword: string, action: 'archive' | 'unarchive') =>
    api.patch<{ keyword: string; updated: number }>(`/alibaba/searches/by-keyword/${encodeURIComponent(keyword)}/archive`, { action }),

  /** Get insights summary for all tags */
  getInsightsSummary: () =>
    api.get<InsightsSummaryItem[]>('/alibaba/insights/summary'),

  /** Get paginated products by tag */
  getProductsByTag: (tag: string, page: number = 1, limit: number = 50) =>
    api.get<ProductsByTagResponse>(`/alibaba/insights/by-tag/${encodeURIComponent(tag)}?page=${page}&limit=${limit}`),

  /** Batch parse products under a tag */
  parseProducts: (tag: string) =>
    api.post<{
      total: number; parsed: number; partial: number; failed: number;
      skipped: number; new_attrs: number;
      module_coverage: Record<string, string>;
      warnings: string[];
    }>('/alibaba/parse-products', { tag }),

  /** Get insights report for a tag */
  getInsights: (tag: string) =>
    api.get<InsightsReport>(`/alibaba/insights/${encodeURIComponent(tag)}`),

  /** Download Excel report for a tag */
  downloadExcel: (tag: string) => {
    window.open(`/api/alibaba/insights/${encodeURIComponent(tag)}/excel`, '_blank')
  },

  /** Download insights Excel export for a tag (4-sheet xlsx) */
  downloadInsightsExcel: (tag: string) => {
    window.open(`/api/alibaba/insights/${encodeURIComponent(tag)}/excel`, '_blank')
  },

  /** Get pipeline status for a tag (Task B API — may not exist yet) */
  getPipelineStatus: (tag: string) =>
    api.get<PipelineStatus>(`/alibaba/pipeline-status/${encodeURIComponent(tag)}`),

  /** Trigger batch recrawl for failed tasks under a tag */
  triggerRecrawl: (tag: string) =>
    api.post<{
      message: string;
      total_failed: number;
      batch_size?: number;
      status?: string;
    }>('/alibaba/recrawl-batch', { tag }),

  /** Trigger single iframe URL recrawl via BrowserAct */
  recrawlIframe: (taskId: string, iframeUrl: string) =>
    api.post<{
      task_id: string;
      success: boolean;
      html_size: number;
      status: string;
    }>('/alibaba/recrawl-iframe', { task_id: taskId, iframe_url: iframeUrl }),

  /** Get distinct keywords (for export picker) */
  keywords: () =>
    api.get<string[]>('/alibaba/keywords'),

  /** Export search results or product details as CSV/Excel/JSON */
  exportData: (params: { keyword: string; type?: 'search_results' | 'product_details' | 'full_details'; format?: 'csv' | 'excel' | 'json' }) => {
    const qs = new URLSearchParams();
    qs.set('keyword', params.keyword);
    if (params.type) qs.set('type', params.type);
    if (params.format) qs.set('format', params.format);
    return api.download(`/alibaba/export?${qs.toString()}`);
  },

  /** Retry a single failed search page */
  retrySearch: (searchId: string) =>
    api.post<{ search_id: string; status: string; message: string }>(`/alibaba/searches/${searchId}/retry`),

  /** Retry all failed pages for a keyword */
  retryFailedByKeyword: (keyword: string) =>
    api.post<{ keyword: string; retried: number; message: string }>('/alibaba/searches/retry-failed', { keyword }),

  /** Retry all unhealthy pages for a keyword (warning/error health status) */
  retryUnhealthyByKeyword: (keyword: string) =>
    api.post<{ keyword: string; retried: number; message: string }>('/alibaba/searches/retry-unhealthy', { keyword }),

  /** Get Google Sheets auth URL */
  getSheetsAuthUrl: () =>
    api.get<string>('/google-sheets/auth-url'),

  /** Export Alibaba data to Google Sheets */
  exportToSheets: (data: { keyword: string; accessToken: string; refreshToken?: string }) =>
    api.post<{
      spreadsheetId: string;
      url: string;
      title: string;
      sheetCount: number;
      rowCount: number;
    }>('/google-sheets/export-alibaba', data),

  /** Bind keyword → existing Google Sheet for auto-sync */
  bindSheet: (data: {
    sheet_url: string; keyword: string; tab_name?: string;
    sync_interval_hours?: number; access_token: string; refresh_token?: string;
  }) =>
    api.post<{ id: number; sheet_id: string; keyword: string; tab_name: string; created_at: string }>(
      '/google-sheets/bindings', data
    ),

  /** List all sheet bindings */
  listBindings: () =>
    api.get<Array<{
      id: number; sheet_id: string; sheet_url: string; tab_name: string;
      keyword: string; sync_interval_hours: number;
      last_synced_at: string | null; last_sync_rows: number | null;
      last_sync_device: string | null; last_sync_error: string | null;
      is_active: boolean; created_at: string;
    }>>('/google-sheets/bindings'),

  /** Delete a binding */
  deleteBinding: (id: number) =>
    api.delete(`/google-sheets/bindings/${id}`),

  /** Manually trigger sync for a binding */
  syncBinding: (id: number, device?: string) =>
    api.post<{ rows: number; synced_at: string }>(`/google-sheets/bindings/${id}/sync`, { device }),

  /** Export SERP Analysis data (40 cols) to an existing Google Sheet tab */
  exportAnalysisToSheets: (data: {
    projectId: string; sheetUrl: string; tabName?: string;
    requireAllSteps?: boolean; accessToken: string; refreshToken?: string;
  }) =>
    api.post<{ rows: number; sheetUrl: string; tabName: string }>(
      '/google-sheets/export-serp-analysis', data
    ),

  /** Get data-viewer keywords with completeness stats */
  dataViewerKeywords: () =>
    api.get<Array<{
      keyword: string; product_count: number; profile_count: number;
      parsed_ok: number; parsed_failed: number; parsed_partial: number;
      coverage_pct: number;
    }>>('/alibaba/data-viewer/keywords'),

  /** Get merged A+B data for data-viewer */
  dataViewer: (params: { keyword: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    qs.set('keyword', params.keyword);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    return api.get<{
      summary: {
        totalProducts: number; totalCompanies: number; avgPrice: number; attrCount: number;
        levelA: number; levelAB: number; parsedOk: number; parsedPartial: number;
        parsedFailed: number; hasImages: number;
      };
      attrColumns: Array<{ key: string; coverage: number; total: number; pct: number }>;
      rows: Array<Record<string, unknown>>;
    }>(`/alibaba/data-viewer?${qs.toString()}`);
  },

  /** Parse diagnostics: module success rates per keyword */
  parseDiagnostics: (keyword: string) =>
    api.get<{
      totalParsed: number;
      modules: Array<{
        module: string;
        selector: string;
        success: number;
        failed: number;
        successRate: number;
      }>;
      failedProducts: Array<{
        profileId: number;
        taskId: string;
        productName: string;
        productUrl: string;
        parseStatus: string;
        missingModules: string[];
      }>;
    }>(`/alibaba/data-viewer/parse-diagnostics?keyword=${encodeURIComponent(keyword)}`),

  /** Re-parse all profiles from existing schema + HTML (no re-crawl) — batched POST fallback */
  reparseAll: () =>
    api.post<{
      total: number;
      updated: number;
      failed: number;
      skipped: number;
    }>('/alibaba/reparse-all', {}),

  /** SSE stream URL for batched re-parse with real-time progress */
  reparseAllStreamUrl: '/alibaba/reparse-all-stream',

  // ── Pipeline M4 API ──

  /** Get pipeline steps for a task */
  getTaskPipeline: (taskId: string) =>
    api.get<PipelineStep[]>(`/tasks/${taskId}/pipeline`),

  /** Retry a specific pipeline step */
  retryPipelineStep: (taskId: string, stepId: string) =>
    api.post<{ success: boolean; message: string }>(`/tasks/${taskId}/pipeline/retry/${stepId}`),

  /** Get pipeline overview (all tasks) */
  getPipelineOverview: () =>
    api.get<PipelineOverviewItem[]>('/alibaba/pipeline/overview'),

  /** Batch advance all eligible pipeline steps */
  batchAdvancePipeline: () =>
    api.post<{ advanced: number; message: string }>('/alibaba/pipeline/batch-advance'),

  /** Get keyword × pipeline stage completion matrix */
  getKeywordMatrix: (filters?: { project_id?: string; group_id?: string; tag?: string; search?: string; url_contains?: string; sort_by?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams()
    if (filters?.project_id)   qs.set('project_id',   filters.project_id)
    if (filters?.group_id)     qs.set('group_id',     filters.group_id)
    if (filters?.tag)          qs.set('tag',           filters.tag)
    if (filters?.search)       qs.set('search',        filters.search)
    if (filters?.url_contains) qs.set('url_contains',  filters.url_contains)
    if (filters?.sort_by)      qs.set('sort_by',       filters.sort_by)
    if (filters?.limit  != null) qs.set('limit',  String(filters.limit))
    if (filters?.offset != null) qs.set('offset', String(filters.offset))
    const q = qs.toString()
    return api.get<KeywordMatrixRow[]>(`/alibaba/pipeline/keyword-matrix${q ? `?${q}` : ''}`)
  },

  // ── iframe & ShareMyImage API ──

  /** Get ShareMyImage album stats */
  getShareMyImageStats: () =>
    api.get<ShareMyImageAlbumStats[]>('/admin/sharemyimage/stats'),

  /** Get iframe coverage stats for dashboard */
  getIframeCoverage: () =>
    api.get<{
      total_alibaba_tasks: number;
      with_iframe_images: number;
      coverage_pct: number;
    }>('/alibaba/iframe-coverage'),

  /** WIN GUI Worker real-time status — which laptop is scraping what */
  getGuiWorkerStatus: () =>
    api.get<Array<{
      device_name: string;
      current_url: string | null;
      product_name: string | null;
      status: 'idle' | 'processing';
      started_at: string | null;
      updated_at: string;
    }>>('/alibaba/gui-worker-status'),

  /** Retry iframe pipeline (C-3 to C-5) for a task */
  retryIframePipeline: (taskId: string) =>
    api.post<{ success: boolean; message: string }>(`/tasks/${taskId}/pipeline/retry-iframe`),

  /** Get URL detail for a keyword × stage (for cell drill-down modal) */
  getKeywordUrlDetail: (keyword: string, stage: 'a1' | 'a2' | 'a3', project_id?: string) => {
    const qs = new URLSearchParams({ keyword, stage });
    if (project_id) qs.set('project_id', project_id);
    return api.get<UrlDetailItem[]>(`/alibaba/pipeline/keyword-url-detail?${qs}`);
  },

  /** Get per-task breakdown for a keyword × Ali pipeline step (hover tooltip) */
  getKeywordStepDetail: (keyword: string, step_id: string, project_id?: string) => {
    const qs = new URLSearchParams({ keyword, step_id });
    if (project_id) qs.set('project_id', project_id);
    return api.get<AliStepDetailResponse>(`/alibaba/pipeline/keyword-step-detail?${qs}`);
  },

  /** PD step hover tooltip — queries correct DB table per PD-1~PD-10 step */
  getAliPdStepTooltip: (keyword: string, pd_step: number, project_id?: string) => {
    const qs = new URLSearchParams({ keyword, pd_step: String(pd_step) });
    if (project_id) qs.set('project_id', project_id);
    return api.get<AliPdStepTooltipResponse>(`/alibaba/pipeline/ali-pd-step-tooltip?${qs}`);
  },

  /** Queue incomplete tasks for priority crawling */
  priorityCrawl: (task_ids: string[], reset_failed = true) =>
    api.post<{ queued: number; total: number }>('/alibaba/pipeline/priority-crawl', { task_ids, reset_failed }),

  /** Reset Alibaba pipeline steps to pending for keywords × stage_ids (補跑 Ali) */
  retrigerAliStages: (keywords: string[], stageIds: string[]) =>
    api.post<{ reset: number; task_count: number; stage_ids: string[]; message: string }>(
      '/alibaba/pipeline/retrigger-stages',
      { keywords, stage_ids: stageIds },
    ),

  /** AI-cluster similar attribute values via Straico LLM (single attr, saves to DB) */
  normalizeAttrValues: (tag: string, attrKey: string, rawValues: Array<{ value: string; count: number }>) =>
    api.post<Array<{ value: string; count: number }>>(`/alibaba/insights/${encodeURIComponent(tag)}/normalize-attrs`, { attrKey, rawValues }),

  /** Load all saved AI summaries for a tag (includes stale detection) */
  getAiSummaries: (tag: string) =>
    api.get<AiSummaryRecord[]>(`/alibaba/insights/${encodeURIComponent(tag)}/ai-summaries`),

  /** Batch AI-summarize all (or only stale) attributes for a tag */
  batchAiSummaries: (tag: string, onlyStale = false) =>
    api.post<{ done: boolean; processed: number; failed: number; total: number }>(
      `/alibaba/insights/${encodeURIComponent(tag)}/batch-ai-summaries`,
      { onlyStale },
    ),

  /** Get per-keyword Alibaba category page crawl progress (路徑 A: 分類頁) */
  getAliSearchProgress: (project_id?: string) => {
    const qs = new URLSearchParams();
    if (project_id) qs.set('project_id', project_id);
    return api.get<AliSearchProgressRow[]>(`/alibaba/pipeline/ali-search-progress${qs.toString() ? `?${qs}` : ''}`);
  },

  /** Get per-keyword company page crawl progress (路徑 C: 公司頁) */
  getAliCompanyProgress: (project_id?: string) => {
    const qs = new URLSearchParams();
    if (project_id) qs.set('project_id', project_id);
    // apiFetch returns res.data = json.data (rows array); table_exists is at top-level of json
    return api.get<AliCompanyProgressRow[]>(`/alibaba/pipeline/ali-company-progress${qs.toString() ? `?${qs}` : ''}`);
  },

  /** Get paginated URLs for a keyword with their full A-1~C-5 pipeline step status (Tab3 PD Panel) */
  getKeywordAllSteps: (keyword: string, projectId?: string, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ keyword, limit: String(limit), offset: String(offset) })
    if (projectId) params.set('project_id', projectId)
    return api.get<KeywordAllStepsResponse>(`/alibaba/pipeline/keyword-all-steps?${params}`)
  },

  /** SC Drill-down: per-page breakdown for a keyword (ali-sc-pages) */
  getAliScPages: (keyword: string, projectId?: string) => {
    const params = new URLSearchParams({ keyword })
    if (projectId) params.set('project_id', projectId)
    // apiFetch extracts json.data = rows array; keyword is available via (res as any).keyword
    return api.get<ScPageRow[]>(`/alibaba/pipeline/ali-sc-pages?${params}`)
  },

  /** PD Drill-down: per-product stage_status for a keyword (ali-pd-products) */
  getAliPdProducts: (keyword: string, projectId?: string, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ keyword, limit: String(limit), offset: String(offset) })
    if (projectId) params.set('project_id', projectId)
    return api.get<PdProductRow[]>(
      `/alibaba/pipeline/ali-pd-products?${params}`
    )
  },

  /** CO Drill-down: per-company status for a keyword (ali-co-companies) */
  getAliCoCompanies: (keyword: string, projectId?: string) => {
    const params = new URLSearchParams({ keyword })
    if (projectId) params.set('project_id', projectId)
    // apiFetch extracts json.data = rows array; table_exists is at top-level of json
    return api.get<CoCompanyRow[]>(`/alibaba/pipeline/ali-co-companies?${params}`)
  },

  /** CO Nav categories detail: list nav categories for a single company URL */
  getAliCoNavCategories: (company_url: string) => {
    const params = new URLSearchParams({ company_url })
    return api.get<CoNavCategory[]>(`/alibaba/pipeline/ali-co-nav-categories?${params}`)
  },

  /** Ali PD Keyword Matrix: per-keyword aggregation from alibaba_searches (replaces keyword-matrix for PD tab) */
  getAliPdMatrix: (params: {
    project_id?: string
    search?: string
    sort_by?: 'keyword' | 'completion'
    limit?: number
    offset?: number
  }) => {
    const qs = new URLSearchParams()
    if (params.project_id) qs.set('project_id', params.project_id)
    if (params.search)     qs.set('search', params.search)
    if (params.sort_by)    qs.set('sort_by', params.sort_by)
    qs.set('limit',  String(params.limit  ?? 50))
    qs.set('offset', String(params.offset ?? 0))
    return api.get<AliPdMatrixRow[]>(`/alibaba/pipeline/ali-pd-keyword-matrix?${qs}`)
  },

  /** SC: retry failed/pending search pages for a keyword */
  retryScPages: (keyword: string, mode: 'failed' | 'pending' | 'all' = 'failed') =>
    api.post<{ success: boolean; data: { keyword: string; retried: number; message: string } }>(
      `/alibaba/searches/retry-failed`,
      { keyword, mode }
    ),

  /** CO: queue company profiles for crawling */
  batchCrawlCompanies: (keyword: string, limit = 20) =>
    api.post<{ success: boolean; queued: number; message?: string }>(
      `/alibaba/company-profiles/batch-crawl`,
      { keyword, limit }
    ),

  /** CO: crawl product listing pages for completed company profiles */
  batchCrawlCoProducts: (keyword: string, limit = 5) =>
    api.post<{ success: boolean; crawled: number; total_products: number }>(
      `/alibaba/company-profiles/batch-crawl-products`, { keyword, limit }
    ),

  /** Competitive supplier analysis for a keyword */
  getSuppliersAnalysis: (keyword: string) =>
    api.get<{
      success: boolean
      keyword: string
      country_distribution: Array<{ country_code: string; count: number; avg_rating: number | null }>
      top_suppliers: Array<{
        company_id: string; company_name: string; country_code: string | null
        rating_score: number | null; reviews_count: number | null
        on_time_delivery: number | null; gold_supplier: boolean | null
        certifications: string[] | null; search_rank: number | null
      }>
      summary: { gold_count: number; factory_count: number; total: number }
    }>(`/alibaba/suppliers/analysis`, { params: { keyword } }),

  /** SC: batch crawl Suppliers Tab for a keyword via BrowserAct */
  batchCrawlSuppliers: (keyword: string, limit = 5) => {
    return api.post<{ success: boolean; pages_crawled: number; total_cards: number; message?: string }>(
      `/alibaba/suppliers/batch-crawl`,
      { keyword, limit }
    )
  },

  /** SC: list supplier rows for a keyword (ali_supplier_results JOIN ali_companies) */
  getSuppliersByKeyword: (keyword: string, limit = 50, offset = 0) => {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    return api.get<{
      success: boolean;
      data: {
        keyword: string;
        total: number;
        limit: number;
        offset: number;
        suppliers: Array<{
          id: number;
          company_id: string;
          search_rank: number | null;
          page_number: number | null;
          is_top_rank: boolean;
          captured_at: string;
          company_name: string | null;
          company_subdomain: string | null;
          company_logo_url: string | null;
          country_code: string | null;
          supplier_type: string | null;
          gold_supplier: boolean | null;
          is_factory: boolean | null;
          rating_score: number | null;
          reviews_count: number | null;
          certifications: string[] | null;
          main_products: string[] | null;
          main_product_price: string | null;
        }>;
      };
    }>(`/alibaba/suppliers/by-keyword/${encodeURIComponent(keyword)}?${qs}`);
  },

  /** SC Suppliers stats for a keyword */
  getScSuppliersStats: (keyword: string) =>
    api.get<{ success: boolean; keyword: string; data: { total_suppliers: number; crawled: number; country_count: number; avg_rating: number } }>(
      `/alibaba/pipeline/ali-sc-suppliers-stats`, { params: { keyword } }
    ),

  /** PD: trigger batch C-2 BrowserAct iframe fetch */
  triggerIframeFetch: () =>
    api.post<{ success: boolean; data: { status: string; message: string } }>(
      `/alibaba/iframe-pipeline/batch-iframe-fetch`,
      {}
    ),

  /** PD: backfill C-4 MarkupGo screenshot for tasks with iframe HTML but no screenshot */
  backfillC4: (limit = 10) =>
    api.post<{ success: boolean; triggered: number; limit: number }>(
      `/alibaba/iframe-pipeline/backfill-c4`,
      { limit }
    ),

  /** CO: BA 補爬廠區資訊（cpProductionCapacity / cpTradeCapability / cpRDCapacity） */
  batchBaCrawlCompanies: (keyword: string, limit = 3) =>
    api.post<{ success: boolean; crawled: number; keyword: string | null }>(
      `/alibaba/company-profiles/batch-ba-crawl`,
      { keyword, limit }
    ),

  /** Alibaba three-layer coverage overview (SC + CO + PD + QS savings) */
  getAlibabaOverview: () =>
    api.get<{
      success: boolean
      sc: { keywords_total: number; searches_done: number; suppliers_found: number; nav_categories: number }
      co: { total: number; qs_done: number; qs_pending: number; ba_done: number; has_factory: number; has_products: number }
      pd: { total: number; ba_parsed: number; qs_sufficient: number; has_markdown: number; has_screenshot: number; captcha_blocked: number }
      qs_savings: { ba_credits_saved: number; qs_rate_pct: number }
      generated_at: string
    }>('/alibaba/pipeline/alibaba-overview'),

  /** Trigger a backend action (CO crawl / BA factory / CO products / C-4 backfill) */
  triggerAlibabaAction: (action: 'trigger_co_crawl' | 'trigger_co_products' | 'trigger_ba_factory' | 'trigger_c4_backfill', keyword?: string, limit?: number) =>
    api.post<{ success: boolean; action: string; message: string }>(
      '/alibaba/pipeline/alibaba-actions',
      { action, keyword, limit }
    ),

  /** Get a single product profile by numeric task_id */
  getProductProfile: async (taskId: string) => {
    const res = await fetch(`/api/alibaba/products/${taskId}`);
    if (!res.ok) throw new Error(`Product not found: ${taskId}`);
    return res.json() as Promise<{ data: AlibabaProductProfile }>;
  },

  /** Get a single product profile by product URL */
  getProductProfileByUrl: async (url: string) => {
    const res = await fetch(`/api/alibaba/products/by-url?url=${encodeURIComponent(url)}`);
    if (!res.ok) throw new Error(`Product not found`);
    return res.json() as Promise<{ data: AlibabaProductProfile }>;
  },

  /** Generate LLM executive summary for management reporting (規格屬性要點 + 競品要點 + 管理摘要) */
  generateIndustryAnalysis: (tag: string) =>
    api.post<{
      spec_key_points: string[]
      competitor_key_points: string[]
      executive_summary: string[]
    }>(`/alibaba/insights/${encodeURIComponent(tag)}/industry-analysis`, {}),
};

// ── AlibabaProductProfile (alibaba_product_profiles table) ──

export interface AlibabaProductProfile {
  id: number;
  task_id: string;
  product_url: string;
  product_name: string | null;
  company_name: string | null;
  price_min: number | null;
  price_max: number | null;
  price_currency: string | null;
  sku: string | null;
  moq_quantity: number | null;
  lead_time_min: number | null;
  lead_time_max: number | null;
  iframe_url: string | null;
  variations: any[] | null;
  customization_options: any[] | null;
  highlights: string[] | null;
  price_tiers: any[] | null;
  related_searches: string[] | null;
  smi_image_urls: string[] | null;
  html_full_markdown: string | null;
  html_full_image_urls: string[] | null;
  iframe_image_urls: string[] | null;
  iframe_image_count: number | null;
  iframe_description_text: string | null;
  scraped_at: string | null;
  scraped_by: string | null;
  updated_at: string | null;
  parsed_at: string | null;
}

// ── iframe data types ──

export interface IframeData {
  desc_iframe_url: string | null;
  desc_iframe_status: string | null;
  desc_iframe_screenshot_url: string | null;
  iframe_image_urls: string[];
  iframe_image_count: number;
}

export interface ShareMyImageAlbumStats {
  album_id: string;
  album_name: string;
  used: number;
  limit: number;
  status: 'ok' | 'warning' | 'full';
}

// ── Pipeline Keyword Matrix type ──

export interface KeywordMatrixRow {
  keyword: string;
  top_total: number;
  non_top_total: number;
  a1_top: number; a1_non: number;
  a2_top: number; a2_non: number;
  a3_top: number; a3_non: number;
  a4_top: number; a4_non: number;
  b1_top: number; b1_non: number;
  b2_top: number; b2_non: number;
  b3_top: number; b3_non: number;
  b4_top: number; b4_non: number;
  c1_top: number; c1_non: number;
  c2_top: number; c2_non: number;
  c3_top: number; c3_non: number;
  c4_top: number; c4_non: number;
  c5_top: number; c5_non: number;
  /** Overall A-1/A-2/A-3 completion % across TOP+non-TOP (0-100, 1 decimal) */
  completion_pct: number | null;
  // Tab readiness (SERP分析 A4 mode)
  serp_result_count: number;
  nav_top_count: number;
  seo_top_count: number;
  paa_count: number;
  ngram_count: number;
  rank_count: number;
}

// ── Pipeline step type (M4 Gantt) ──

export interface PipelineStep {
  step_id: string;
  step_name: string;
  series: string;
  step_order: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  output_summary: string | null;
  error_message: string | null;
  depends_on: string[];
  unlocks: string[];
}

export interface PipelineOverviewItem {
  task_id: string;
  url: string;
  steps: PipelineStep[];
  completed: number;
  total: number;
  failed: number;
}

// ── Pipeline status type ──

export interface PipelineStatus {
  tag: string;
  total: number;
  scraped: number;
  parsed: number;
  failed: number;
  needs_recrawl: number;
}

// ── Summary types (Stage 10-B) ──

export interface InsightsSummaryItem {
  tag: string;
  total_products: number;
  parsed: number;
  partial: number;
  failed: number;
  no_profile: number;
  progress_pct: number;
  avg_price_min: number | null;
  avg_price_max: number | null;
  top_countries: string[];
  trade_assurance_pct: number;
}

export interface ProductListItem {
  id: number;
  task_id: string;
  product_name: string | null;
  product_url: string;
  company_name: string | null;
  company_country: string | null;
  price_min: number | null;
  price_max: number | null;
  moq_text: string | null;
  has_trade_assurance: boolean;
  parse_status: string;
  parsed_at: string | null;
}

export interface ProductsByTagResponse {
  items: ProductListItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

// ── 斷鏈監控 ──────────────────────────────────────────────────
export interface BrokenChainStats {
  total_broken: number;
  total_fixed: number;
  recent_fixed_1h: number;
  by_machine: Array<{ worker_id: string; broken_count: number }>;
  keywords_ready: number;
  keywords_total: number;
  generated_at: string;
}

export async function getBrokenChainStats(): Promise<BrokenChainStats> {
  const res = await fetch('/api/alibaba/pipeline/broken-chain-stats');
  if (!res.ok) throw new Error('broken-chain-stats fetch failed');
  return res.json();
}

// ── AI Summary types ──

export interface AiSummaryRecord {
  attr_key: string;
  summarized_values: Array<{ value: string; count: number }>;
  source_product_count: number;
  source_value_count: number;
  model_used: string | null;
  run_at: string;
  stale: boolean;
  current_product_count: number;
}

// ── Insights types (matches backend InsightsReport) ──

export interface InsightsReport {
  tag: string;
  overview: {
    total_products: number;
    total_companies: number;
    countries: number;
    avg_price: number;
  };
  price_analysis: {
    min: number;
    max: number;
    avg: number;
    histogram: Array<{ range: string; count: number }>;
  };
  attribute_coverage: Array<{
    attr_key: string;
    count: number;
    values: Array<{ value: string; count: number }>;
  }>;
  /** Pre-normalization coverage grouped by original attr_key */
  raw_attribute_coverage?: Array<{
    attr_key: string;
    count: number;
    values: Array<{ value: string; count: number }>;
  }>;
  supplier_distribution: {
    by_country: Array<{ country: string; count: number }>;
    by_years: Array<{ range: string; count: number }>;
  };
  seo_coverage: {
    title: { count: number; total: number };
    meta_desc: { count: number; total: number };
    h1: { count: number; total: number };
    schema: { count: number; total: number };
  };
  competitor_list: Array<{
    company_name: string;
    country: string | null;
    years: number | null;
    products: number;
    price_range: string;
    has_trade_assurance: boolean;
    company_url: string | null;
    year_established: number | null;
    annual_revenue: string | null;
    rating: number | null;
    review_count: number | null;
    certifications_json: Array<{ certificateName?: string; [key: string]: unknown }> | null;
  }>;
  co_insights?: {
    co_total: number;
    co_qs_done: number;
    co_ba_done: number;
    co_has_factory: number;
    co_gold_count: number;
    co_factory_count: number;
    co_trade_assurance: number;
    avg_transactions: number | null;
    top_certifications: string[] | null;
    country_breakdown: Array<{ country: string; count: number }> | null;
  };
  iframe_insights?: {
    pd_total: number;
    has_iframe_url: number;
    has_screenshot: number;
    has_markdown: number;
    captcha_blocked: number;
    has_iframe_images: number;
    avg_iframe_images: number | null;
    screenshot_sample: string[] | null;
    top_products_with_media: Array<{
      task_id: string;
      product_name: string | null;
      price_min: number | null;
      company_name: string | null;
      screenshot_url: string;
      markdown_preview: string | null;
      iframe_image_count: number;
    }>;
  };
}

// ── Product Profile Direct Lookup (local-sync products) ──

export interface AlibabaProductProfile {
  id: number;
  task_id: string;
  product_url: string;
  product_name: string | null;
  company_name: string | null;
  price_min: number | null;
  price_max: number | null;
  price_currency: string | null;
  sku: string | null;
  moq_quantity: number | null;
  lead_time_min: number | null;
  lead_time_max: number | null;
  iframe_url: string | null;
  variations: Array<{ name: string; options: string[] }> | null;
  customization_options: string[] | null;
  highlights: string[] | null;
  price_tiers: Array<{ min_qty: number; max_qty: number | null; unit_price: number }> | null;
  related_searches: string[] | null;
  smi_image_urls: string[] | null;
  html_full_markdown: string | null;
  html_full_image_urls: string[] | null;
  html_full_video_urls: string[] | null;
  iframe_image_urls: string[] | null;
  iframe_image_count: number | null;
  scraped_at: string | null;
  scraped_by: string | null;
  updated_at: string | null;
  parsed_at: string | null;
}

/** Get product profile by numeric task_id (local-sync products) */
export async function getProductProfile(taskId: string): Promise<AlibabaProductProfile> {
  const res = await fetch(`/api/alibaba/products/${taskId}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Product not found: ${taskId} (${res.status})`);
  const json = await res.json();
  return json.data as AlibabaProductProfile;
}

/** Get product profile by product URL */
export async function getProductProfileByUrl(url: string): Promise<AlibabaProductProfile> {
  const res = await fetch(`/api/alibaba/products/by-url?url=${encodeURIComponent(url)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Product not found (${res.status})`);
  const json = await res.json();
  return json.data as AlibabaProductProfile;
}
